import React, { useRef, useEffect } from "react"
import { useForm } from "react-hook-form"
import {
  Flex,
  Box,
  Text,
  Input,
  Spacer,
  chakra,
  Tooltip,
  HStack,
} from "@chakra-ui/react"
import posToStr from "../../functions/utils/posToStr"
import {
  BiPlus,
  BiMinus,
  BiLeftArrowAlt,
  BiUpArrowAlt,
  BiRightArrowAlt,
  BiDownArrowAlt,
  BiCaretLeft,
  BiCaretDown,
  BiArrowToRight,
  BiArrowToBottom,
} from "react-icons/bi"
import { RiLockUnlockLine, RiLockLine } from "react-icons/ri"
import TrafficLight from "../TrafficLight"
import SwitchRound from "../SwitchRound"
import SelectPerson from "../SelectPerson"

import * as WIDTH from "../../const/widths"
import { ROOT } from "../../const/globals"

import {
  rowCreate,
  rowUpdate,
  rowDelete,
  rowMoveIn,
  rowMoveOut,
  rowMoveUpDown,
  rowFold,
  rowIgnore,
  rowForecast,
  rowQuality,
  rowUnresolved,
  isByWhenPinnedUpdate,
  // isFromWhenPinnedUpdate,
  isHighlightedUpdate,
} from "../../redux/actions/project"
import { levelColorSet } from "../../functions/helpers/levelColorSet"
import { durToDurStr } from "../../functions/timeHandler/durations"
import {
  tpAbbrD,
  tpAbbrHS,
  tpAbbrHE,
} from "../../functions/timeHandler/timePointAbbreviate"
import locToISO from "../../functions/timeHandler/locToISO"
import {
  ABBR_D_MESSAGE,
  ABBR_D_REGEX,
  ABBR_H_MESSAGE,
  ABBR_H_REGEX,
  PERCENT_MESSAGE,
  PERCENT_REGEX,
  DURATION_MESSAGE,
  DURATION_REGEX,
  POSITION_MESSAGE,
  POSITION_REGEX,
} from "../../const/messages"
import objDeepTrim from "../../functions/utils/objDeepTrim"

const SheetRow = ({
  row,
  node,
  isTop = false,
  isBottom = false,
  dateStatusCurrent,
  isStatusLocked,
  params,
  isResetForm = false,
  dispatch,
  isExamOn,
  isDebugOn,
}) => {
  //=================================================================

  const inputRef = useRef(null)
  const { register, handleSubmit, reset, errors } = useForm()

  const {
    nId,
    pId,
    children,
    position,
    who,
    deliversWhat,
    fromWhen,
    toProjectionISO,
    byWhen,
    isByWhenPinned,
    toWhom,
    deadline,
    isDeadlineDue,
    //
    degree,
    isDelivered,
    forecast,
    quality,
    comment,
    isCommentRequired,
    precedents,
    precedentsStr,
    dependents,
    //
    span,
    projection,
    slack,
    spent,
    spentStr,
    projectionStr,
    spanStr,
    slackStr,
    fromSlackISO,
    //
    isIgnored,
    isUnresolved,
    isHighlighted,
    // isSelected,
    isFolded,
    levelMax,
  } = node

  const { isDetailsOn, isCommentVertical, dayEnd } = params

  const hasChildren = children.length > 0 || nId === ROOT
  const hasPosition = position.length > 0
  const isMovedIn = position.length > 1
  const level = position.length

  const isLocked = node.isLocked || isStatusLocked

  // ----------------------------------------------------------------
  useEffect(() => {
    if (isResetForm) {
      reset()
    }
  }, [isResetForm, reset, node])

  // ----------------------------------------------------------------
  const dataRemoveUnchanged = (data) => {
    // console.log("dataRemoveUnchanged: ", { data })
    data.byWhen =
      data.byWhenH !== undefined && data.byWhenH !== ""
        ? data.byWhenD + "|" + data.byWhenH
        : data.byWhenD
    delete data.byWhenD
    delete data.byWhenH
    if (data.byWhen === undefined) {
      delete data.byWhen
    }

    for (const key in data) {
      let valueBefore = node[key]
      // console.log({ key, value: data[key], valueBefore })
      if (key === "byWhen" && !!node[key]) {
        // console.log("dataRemoveUnchanged byWhen")
        if (
          data[key] === "" ||
          locToISO(data[key], dateStatusCurrent) === valueBefore
        ) {
          delete data[key]
        }
      } else if ((key === "spent" || key === "plan") && !!node[key]) {
        // console.log("dataRemoveUnchanged spent || plan")
        valueBefore = durToDurStr(node[key])
        if (data[key] === "" || data[key] === valueBefore) {
          delete data[key]
        }
      } else if (key === "degree" && !!node[key]) {
        // console.log("dataRemoveUnchanged degree")

        // degree is internally an int
        node[key] = parseInt(node[key])
        valueBefore = node[key]
        if (data[key] === "" || data[key] === valueBefore) {
          delete data[key]
        }
      } else if (typeof node[key] !== "string") {
        // TODO: is this possible? Traffic Lights?
        // console.log("dataRemoveUnchanged no string")
        valueBefore = `${valueBefore}`
        if (data[key] === valueBefore) {
          delete data[key]
        }
      } else if (data[key] === valueBefore) {
        // console.log("dataRemoveUnchanged else")
        delete data[key]
      }
    }

    return data
  }
  // ----------------------------------------------------------------
  const onSubmit = (data) => {
    // console.log("SheetRows: ", { ...data })
    inputRef.current.focus()
    objDeepTrim(data)
    const payload = { nId, data: dataRemoveUnchanged(data) }
    // console.log("SheetRows after payload: ", { ...data })
    dispatch(rowUpdate(payload))
  }
  // ----------------------------------------------------------------
  const onclickHighlighted = () => {
    const payload = { nId }
    dispatch(isHighlightedUpdate(payload))
  }

  const onClickIn = (event) => {
    const payload = { nId }
    dispatch(rowMoveIn(payload))
  }

  const onClickOut = (event) => {
    const payload = { nId }
    dispatch(rowMoveOut(payload))
  }

  const onClickUp = (event) => {
    const payload = { nId, isMoveUp: true }
    dispatch(rowMoveUpDown(payload))
  }

  const onClickDown = (event) => {
    const payload = { nId, isMoveUp: false }
    dispatch(rowMoveUpDown(payload))
  }

  const onClickFold = (event) => {
    const payload = { nId }
    dispatch(rowFold(payload))
  }

  // ----------------------------------------------------------------
  const onClickIgnore = (event) => {
    const payload = { nId }
    dispatch(rowIgnore(payload))
  }

  // ----------------------------------------------------------------
  const onClickUnresolved = () => {
    const payload = { nId }
    dispatch(rowUnresolved(payload))
  }

  // ----------------------------------------------------------------
  const onClickForecast = () => {
    const payload = { nId }
    dispatch(rowForecast(payload))
  }

  // ----------------------------------------------------------------
  const onClickQuality = () => {
    const payload = { nId }
    dispatch(rowQuality(payload))
  }

  // ----------------------------------------------------------------
  const onClickPlus = () => {
    const payload = { nIdBefore: nId }
    dispatch(rowCreate(payload))
  }

  const onClickMinus = () => {
    const payload = { nId }
    dispatch(rowDelete(payload))
  }

  // ----------------------------------------------------------------
  const onClickByWhenPin = () => {
    const payload = { nId }
    dispatch(isByWhenPinnedUpdate(payload))
  }

  // const onClickFromWhenPin = () => {
  //   const payload = { nId }
  //   dispatch(isFromWhenPinnedUpdate(payload))
  // }

  // ----------------------------------------------------------------

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
      <Box
        bg={row % 2 ? "white" : "grey.100"}
        _hover={{ bg: "grey.200" }}
        fontFamily="Fira Sans Condensed"
      >
        <Flex color={isIgnored ? "grey.400" : "inherit"}>
          <Box w={WIDTH.IGNORE} textAlign="center">
            {!hasChildren ? (
              <SwitchRound
                color="black"
                isOn={isIgnored}
                onClick={onClickIgnore}
                isDisabled={isLocked}
              />
            ) : (
              <Box>&nbsp;</Box>
            )}
            {isDetailsOn && isDebugOn && (
              <Text fontSize="sm" fontWeight="normal">
                {row}
              </Text>
            )}
          </Box>

          <Spacer />

          <Box
            w={WIDTH.POSITION}
            px={1}
            {...levelColorSet({ prop: "color", level, levelMax, isIgnored })}
            fontFamily="Inconsolata"
            fontWeight="700"
            textDecoration={isHighlighted ? "underline double" : "none"}
          >
            {hasPosition ? (
              <chakra.button
                type="button"
                onClick={onclickHighlighted}
                textDecoration={isHighlighted ? "underline double" : "none"}
                outline="none"
                textAlign="left"
                fontWeight="700"
              >
                {posToStr(position)}
              </chakra.button>
            ) : (
              <chakra.button
                type="button"
                onClick={onclickHighlighted}
                textDecoration={isHighlighted ? "underline double" : "none"}
                outline="none"
                textAlign="left"
                fontWeight="700"
              >
                TOTAL
              </chakra.button>
            )}
            {isDetailsOn && isDebugOn && (
              <Text fontSize="sm" fontWeight="normal">
                {nId}
              </Text>
            )}
          </Box>

          <Spacer />

          <Box w={WIDTH.UNRESOLVED} textAlign="center">
            {!hasChildren && (
              <SwitchRound
                color="red.500"
                isOn={isUnresolved}
                onClick={onClickUnresolved}
                isDisabled={isLocked}
              />
            )}
          </Box>

          <Spacer />

          <Box w={WIDTH.WHO} px={1}>
            <SelectPerson
              sId={nId}
              name="who"
              value={who}
              isIgnored={isIgnored}
              isLocked={isLocked}
            />
            {isDetailsOn && (
              <Flex
                justify="flex-end"
                fontSize="sm"
                color={isIgnored ? "grey.400" : "grey.700"}
                fontWeight="normal"
              >
                <Text ml={3} mr={1}>
                  From:
                </Text>
                <Text w="75%" textAlign="right">
                  {tpAbbrD(fromWhen, dateStatusCurrent)}
                </Text>
                <Text w="20px" textAlign="right">
                  {tpAbbrHS(fromWhen) !== "" && "|"}
                  {tpAbbrHS(fromWhen)}
                </Text>
              </Flex>
            )}
          </Box>

          <Spacer />

          <Box w={WIDTH.WHAT} px={1}>
            <Input
              type="text"
              name="deliversWhat"
              ref={register}
              defaultValue={deliversWhat}
              isDisabled={isLocked}
            />
            {isDetailsOn && (
              <Flex
                justify="flex-start"
                fontSize="sm"
                color={isIgnored ? "grey.400" : "grey.700"}
                fontWeight="normal"
              >
                <Text ml={3} mr={1}>
                  Proj:
                </Text>
                <Text w="35%">{projectionStr}</Text>
                {!children.length && (
                  <Flex
                    justify="flex-start"
                    fontSize="sm"
                    color={isIgnored ? "grey.400" : "grey.700"}
                    fontWeight="normal"
                  >
                    <Text ml={3} mr={1}>
                      Slack: {slackStr}
                    </Text>
                  </Flex>
                )}
              </Flex>
            )}
          </Box>

          <Spacer />

          <Box w={WIDTH.WHEN} pr={2}>
            <Flex
              {...levelColorSet({ prop: "color", level, levelMax, isIgnored })}
              justify="space-between"
            >
              <chakra.button
                onClick={onClickByWhenPin}
                type="button"
                outline="none"
                mb={2}
              >
                {isByWhenPinned ? <BiArrowToBottom /> : <BiArrowToRight />}
              </chakra.button>
              {!hasChildren || isByWhenPinned ? (
                <>
                  <Tooltip
                    isOpen={errors.byWhenD}
                    label={errors.byWhenD ? errors.byWhenD.message : ""}
                    placement="top"
                    hasArrow
                    bg="red.500"
                  >
                    <Input
                      type="text"
                      name="byWhenD"
                      ref={register({
                        pattern: {
                          value: ABBR_D_REGEX,
                          message: ABBR_D_MESSAGE,
                        },
                      })}
                      defaultValue={tpAbbrD(byWhen, dateStatusCurrent)}
                      textAlign="right"
                      isDisabled={isLocked}
                      fontFamily="Inconsolata"
                      fontWeight="700"
                    />
                  </Tooltip>
                  {tpAbbrHE(byWhen, dayEnd) === "" ? (
                    <Box
                      display="inline-block"
                      minW="8px"
                      fontFamily="Inconsolata"
                      fontWeight="700"
                    ></Box>
                  ) : (
                    "|"
                  )}
                  <Tooltip
                    isOpen={errors.byWhenH}
                    label={errors.byWhenH ? errors.byWhenH.message : ""}
                    placement="top"
                    hasArrow
                    bg="red.500"
                  >
                    <Input
                      flex="0 0 auto"
                      w="18px"
                      type="text"
                      name="byWhenH"
                      ref={register({
                        pattern: {
                          value: ABBR_H_REGEX,
                          message: ABBR_H_MESSAGE,
                        },
                      })}
                      defaultValue={tpAbbrHE(byWhen, dayEnd)}
                      textAlign="right"
                      isDisabled={isLocked}
                      fontFamily="Inconsolata"
                      fontWeight="700"
                    />
                  </Tooltip>
                </>
              ) : (
                <Flex fontFamily="Inconsolata" fontWeight="700">
                  <Text textAlign="right">
                    {tpAbbrD(byWhen, dateStatusCurrent)}
                  </Text>
                  <Text flex="0 0 auto" w="26px" textAlign="right">
                    {tpAbbrHE(byWhen, dayEnd) === "" ? "" : "|"}
                    {tpAbbrHE(byWhen, dayEnd)}
                  </Text>
                </Flex>
              )}
            </Flex>
            {isDetailsOn && (
              <Flex
                justify="flex-start"
                fontSize="sm"
                color={isIgnored ? "grey.400" : "grey.700"}
                fontWeight="normal"
              >
                <Text mx={6} mr={1}>
                  Span:
                </Text>
                <Text>{spanStr}</Text>
              </Flex>
            )}
          </Box>

          <Spacer />

          <Box w={WIDTH.WHO} px={1}>
            <SelectPerson
              sId={nId}
              name="toWhom"
              value={toWhom}
              isIgnored={isIgnored}
              isLocked={isLocked}
            />
          </Box>

          <Spacer />

          <Box w={WIDTH.SPENT} px={1} textAlign="right">
            {hasChildren ? (
              <Text
                {...levelColorSet({
                  prop: "color",
                  level,
                  levelMax,
                  isIgnored,
                })}
              >
                {spentStr}
              </Text>
            ) : (
              <Tooltip
                isOpen={errors.spent}
                label={errors.spent ? errors.spent.message : ""}
                placement="top"
                hasArrow
                bg="red.500"
              >
                <Input
                  type="text"
                  name="spent"
                  ref={register({
                    pattern: {
                      value: DURATION_REGEX,
                      message: DURATION_MESSAGE,
                    },
                  })}
                  defaultValue={spentStr}
                  textAlign="right"
                  {...levelColorSet({
                    prop: "color",
                    level,
                    levelMax,
                    isIgnored,
                  })}
                  isDisabled={isLocked}
                />
              </Tooltip>
            )}
          </Box>

          <Spacer />

          <Box
            w={WIDTH.DEGREE}
            px={1}
            textAlign="right"
            fontFamily="Inconsolata"
            fontWeight="700"
          >
            {hasChildren ? (
              !isExamOn ? (
                <Text
                  {...levelColorSet({
                    prop: "color",
                    level,
                    levelMax,
                    isIgnored,
                  })}
                >
                  {degree.toString()}
                </Text>
              ) : (
                <Text
                  h="24px"
                  w="100%"
                  border={isExamOn ? "2px solid goldenrod" : "none"}
                ></Text>
              )
            ) : (
              <Tooltip
                isOpen={errors.degree}
                label={errors.degree ? errors.degree.message : ""}
                placement="top"
                hasArrow
                bg="red.500"
              >
                <Input
                  type="number"
                  name="degree"
                  ref={register({
                    min: { value: 0, message: "Must be >0." },
                    pattern: {
                      value: PERCENT_REGEX,
                      message: PERCENT_MESSAGE,
                    },
                  })}
                  defaultValue={degree.toString()}
                  textAlign="right"
                  {...levelColorSet({
                    prop: "color",
                    level,
                    levelMax,
                    isIgnored,
                  })}
                  isDisabled={isLocked}
                />
              </Tooltip>
            )}
          </Box>

          <Spacer />

          <Box w={WIDTH.DEADLINE} textAlign="center">
            {((!hasChildren && (isDeadlineDue || degree >= 100)) ||
              (hasChildren && deadline !== 0)) && (
              <TrafficLight
                isExamOn={isExamOn}
                colorCode={deadline}
                isIgnored={isIgnored}
                isDisabled={isLocked}
              />
            )}
          </Box>

          <Spacer />

          <Box w={WIDTH.FORECAST} textAlign="center">
            {(!hasChildren && !isDeadlineDue && degree < 100) ||
            (hasChildren && forecast !== 0) ? (
              !hasChildren ? (
                <TrafficLight
                  isExamOn={isExamOn}
                  colorCode={forecast}
                  onClick={onClickForecast}
                  isIgnored={isIgnored}
                  isDisabled={isLocked}
                />
              ) : (
                <TrafficLight
                  isExamOn={isExamOn}
                  colorCode={forecast}
                  isIgnored={isIgnored}
                  isDisabled={isLocked}
                />
              )
            ) : null}
          </Box>

          <Spacer />

          <Box w={WIDTH.QUALITY} textAlign="center">
            {(!hasChildren && isDelivered) || (hasChildren && quality !== 0) ? (
              !hasChildren ? (
                <TrafficLight
                  isExamOn={isExamOn}
                  colorCode={quality}
                  onClick={onClickQuality}
                  isIgnored={isIgnored}
                  isDisabled={isLocked}
                />
              ) : (
                <TrafficLight
                  isExamOn={isExamOn}
                  colorCode={quality}
                  isIgnored={isIgnored}
                  isDisabled={isLocked}
                />
              )
            ) : null}
          </Box>

          <Spacer />

          {isCommentVertical && (
            <Box w={WIDTH.COMMENT} px={1}>
              <Tooltip
                isOpen={isCommentRequired || errors.comment}
                label={
                  isCommentRequired & (comment.length < 3)
                    ? "Comment required for colors other than green."
                    : errors.comment
                    ? errors.comment.message
                    : ""
                }
                placement="top-start"
                hasArrow
                bg="red.500"
              >
                <Input
                  type="text"
                  name="comment"
                  ref={register({
                    required:
                      isCommentRequired &&
                      "Comment required for colors other than green.",
                  })}
                  // defaultValue={comment + " " + isCommentRequired.toString()}
                  defaultValue={comment}
                  isDisabled={isLocked}
                />
              </Tooltip>

              {isDetailsOn && level > 0 && (
                <Flex
                  justify="flex-end"
                  fontSize="sm"
                  color={isIgnored ? "grey.400" : "grey.700"}
                  fontWeight="normal"
                >
                  <Text ml={3} mr={1}>
                    Precedents:
                  </Text>
                  <Tooltip
                    isOpen={errors.precedentsStr}
                    label={
                      errors.precedentsStr ? errors.precedentsStr.message : ""
                    }
                    placement="top"
                    hasArrow
                    bg="red.500"
                  >
                    <Input
                      type="text"
                      name="precedentsStr"
                      ref={register({
                        pattern: {
                          value: POSITION_REGEX,
                          message: POSITION_MESSAGE,
                        },
                      })}
                      defaultValue={precedentsStr}
                      isDisabled={isLocked}
                    />
                  </Tooltip>
                </Flex>
              )}
            </Box>
          )}

          {isCommentVertical && <Spacer />}

          <Box w={WIDTH.OK} textAlign="center">
            {!isLocked && (
              <chakra.button type="submit" ref={inputRef} outline="none">
                OK
              </chakra.button>
            )}
          </Box>

          <Spacer />

          <Box w={WIDTH.MOVE}>
            {!isLocked && (
              <Flex justify="space-between" align="flex-start">
                {hasChildren ? (
                  isFolded ? (
                    <chakra.button
                      type="button"
                      outline="none"
                      onClick={onClickFold}
                    >
                      <BiCaretLeft />
                    </chakra.button>
                  ) : (
                    <chakra.button
                      type="button"
                      outline="none"
                      onClick={onClickFold}
                    >
                      <BiCaretDown />
                    </chakra.button>
                  )
                ) : (
                  <chakra.button
                    type="button"
                    outline="none"
                    visibility="hidden"
                  >
                    <BiCaretLeft />
                  </chakra.button>
                )}
                {nId !== ROOT ? (
                  <chakra.button
                    type="button"
                    outline="none"
                    onClick={onClickIn}
                  >
                    <BiRightArrowAlt />
                  </chakra.button>
                ) : (
                  <chakra.button
                    type="button"
                    outline="none"
                    visibility="hidden"
                  >
                    <BiRightArrowAlt />
                  </chakra.button>
                )}
                {isMovedIn ? (
                  <chakra.button
                    type="button"
                    outline="none"
                    onClick={onClickOut}
                  >
                    <BiLeftArrowAlt />
                  </chakra.button>
                ) : (
                  <chakra.button
                    type="button"
                    outline="none"
                    visibility="hidden"
                  >
                    <BiLeftArrowAlt />
                  </chakra.button>
                )}

                {!isBottom ? (
                  <chakra.button
                    type="button"
                    outline="none"
                    onClick={onClickDown}
                  >
                    <BiDownArrowAlt />
                  </chakra.button>
                ) : (
                  <chakra.button
                    type="button"
                    outline="none"
                    visibility="hidden"
                  >
                    <BiDownArrowAlt />
                  </chakra.button>
                )}
                {!isTop ? (
                  <chakra.button
                    type="button"
                    outline="none"
                    onClick={onClickUp}
                  >
                    <BiUpArrowAlt />
                  </chakra.button>
                ) : (
                  <chakra.button
                    type="button"
                    outline="none"
                    visibility="hidden"
                  >
                    <BiUpArrowAlt />
                  </chakra.button>
                )}
              </Flex>
            )}
          </Box>

          <Spacer />

          <Flex w={WIDTH.PM} justify="space-between" px={1} align="flex-start">
            {isLocked ? <RiLockLine /> : <RiLockUnlockLine />}

            {nId !== ROOT && !isLocked ? (
              <chakra.button
                type="button"
                onClick={onClickMinus}
                cursor="pointer"
              >
                <BiMinus />
              </chakra.button>
            ) : (
              <Box w="16px"></Box>
            )}
            {!isLocked && (
              <chakra.button
                type="button"
                onClick={onClickPlus}
                cursor="pointer"
              >
                <BiPlus />
              </chakra.button>
            )}
          </Flex>

          {/* <Spacer />

        <Box w={WIDTH.SELECT}>
          <Checkbox name="isSelected" defaultIsChecked={false} />
        </Box> */}
        </Flex>
        {!isCommentVertical && (
          <Flex color="grey.600" fontSize="sm" ml="184px" mr="231px" mb={2}>
            <Input
              // textAlign="right"
              type="text"
              name="comment"
              ref={register}
              defaultValue={comment}
            />
          </Flex>
        )}
        {isDebugOn && (
          <HStack
            color="grey.600"
            fontWeight="normal"
            fontSize="sm"
            ml="184px"
            mr={0}
            mb={2}
            spacing={4}
          >
            <Text>fromWhen:</Text>
            <Text>{fromWhen.replace(":00:00.000Z", "")}</Text>
            <Text>byProj:</Text>
            <Text>{toProjectionISO.replace(":00:00.000Z", "")}</Text>
            <Text>fromSlack:</Text>
            <Text>{fromSlackISO.replace(":00:00.000Z", "")}</Text>
            <Text>byWhen: </Text>
            <Text>{byWhen.replace(":00:00.000Z", "")}</Text>
            <Text>span: </Text>
            <Text>{span.days + ", " + span.hours}</Text>
            <Text>proj: </Text>
            <Text>{projection.days + ", " + projection.hours}</Text>
            <Text>slack: </Text>
            <Text>{slack.days + ", " + slack.hours}</Text>
            <Text>spent: </Text>
            <Text>{spent.days + ", " + spent.hours}</Text>
            <Text>pId: </Text>
            <Text>{pId}</Text>
            <Text>children: </Text>
            <Text>{children.join(", ")}</Text>
            <Text>precedents: </Text>
            <Text>{precedents.join(", ")}</Text>
            <Text>dependents: </Text>
            <Text>{dependents.join(", ")}</Text>
          </HStack>
        )}
      </Box>
    </form>
  )
}

export default SheetRow
